import type { RoutingItem } from '../../App/model/routing-item';
import type { StructureNode } from './structure-node';
import type { AgentSearchContext } from '../../Shell/PandoraSolutionBuilderAgent/model/search-context';
import type { NavigationItemImplementation } from '../../Shell/PandoraSolutionBuilderAgent/model/enumerations';

/**
 * @ngdoc type
 * @name NavigationItem
 * @module mx.sb.Navigation
 *
 * @description
 * An object containing navigation item information (Navigation item model)
 * Constructor accepts title, name, routingItem as params like:
 *
 * ```javascript
 * var navigationItem = new mx.shell.NavigationItem(title, name, routingItem);
 * ```
 *
 * @property {string} title - Gets or sets Display name of the Navigation item
 * @property {string} name - Gets or sets Unique name of the Navigation item
 * @property {string | RoutingItem} routingItem - Gets or sets {@ref RoutingItem routing item} of the navigation item
 * @property {string} icon - Gets or sets Icon associated with Navigation item
 * @property {NavigationItem[]} children - Gets or sets an array of navigation item models that should be displayed under current one
 * @property {object} parameters - Gets or sets object that contains parameters for routing
 * @property {boolean} disabled - Gets or sets flag that defines navigation item as a disabled
 * @property {boolean} openInNewWindow - Gets or sets flag that defines navigation item must open URL in new window or not
 * @property {string} route - Gets route string
 * @property {function} childrenDynamicLoader - if set then this item support dynamic loading for children items.
 *                Expects a method that gets just clicked item as incoming parameter
 *                and returns a promise result of which must be an array of `mx.shell.NavigationItem`s.
 * @property {string} defaultFilter - Gets or sets an id of a default filter for this navigation item
 *
 * ```javascript
 *    var myRootNavItem = new mx.shell.NavigationItem('My Root Nav Item', myRootNavItem);
 *    myRootNavItem.childrenDynamicLoader = function (clickedItem) {
 *		return $http.get('get/clickedItem/children.json').then(function (itemsFromServer) {
 *			return itemsFromServer.map(function () {
 *				return  new mx.shell.NavigationItem(item.title, newItemName, item.routingItem);
 *			});
 *		});
 *	}
 * ```
 */

export type NavigationItemParameters = {
	navigationId: string;
	structures?: string;
	filters?: string;
	appString: string;
};

export interface NavigationCalculationBadge {
	value: string;
	isNumeric: boolean;
	filter?: string;
	title: string;
	onlyCollapsed?: boolean;
	relatedBadges?: NavigationCalculationBadge[];
	amount?: number;
}

export class NavigationItem {
	public parameters?: NavigationItemParameters;
	public id!: string;
	public icon?: string;
	public dataQuery?: string;
	public application?: string;
	public showNestedNavigationInTabs?: boolean;
	public children?: NavigationItem[];
	public hasChildren = false;
	public index?: number;
	public parent?: NavigationItem;
	public disabled = false;
	public selected = false;
	public activated = false;
	public parentOfActivated = false;
	public unwrapped = false;
	public current = false;
	public openInNewWindow = false;
	public availableInSearch = false;
	public matchesFilterCriteria?: boolean;
	public defaultFilter?: string;
	public contentWidgetId?: string;
	public quickFilters?: [string, string, string];
	public showStructureInNavigation = false;
	public basedOnStructure = false;
	public childrenDynamicLoader?: () => Promise<NavigationItem[]>;
	public structure?: StructureNode;
	public searchContext?: AgentSearchContext;
	public calculationBadge?: NavigationCalculationBadge;
	public implementationType?: NavigationItemImplementation;
	public level = 0;
	public description: string = '';
	public isDescrPersonalized?: boolean;
	public _titleLowerCase?: string;
	public favourite = false;
	public showInFavorite = false;
	public owner?: string;
	public htmlClass?: string;
	public isEditableDashboard?: boolean;
	public isHidden?: boolean;

	public get route() {
		return !this.routingItem ? undefined : typeof this.routingItem === 'object' ? this.routingItem.name : this.routingItem;
	}

	constructor(
		public title: string,
		public name: string,
		public technicalName: string,
		public routingItem?: RoutingItem | string
	) {}

	public getClassForNavigationTab() {
		const classes = [`mx-nav-item--${this.name.replaceAll('.', '--')}`];
		if (this.children && this.children.length > 0) {
			classes.push('mx-shell-nav-tabs-level--has-children');
		}
		return classes.join(' ');
	}
}
